/*
 * Painel inicial: o estado da operação de relance.
 *
 * Os contadores respondem "como está agora"; os gráficos, "para onde vai". A
 * comparação com ontem existe porque um número sozinho não diz nada — 47
 * acessos é bom ou ruim dependendo de quantos foram ontem.
 */
import { useEffect, useMemo, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import { nomeDaLocalidade, type DashboardDto, type MikrotikDto } from '../lib/types';
import { GraficoDeFatias, GraficoPorDia } from '../components/Charts';
import { Alert, Card, Spinner } from '../components/ui';
import { ConectadosAgora } from './UsagePanel';

const JANELAS = [7, 30, 90] as const;

const ROTULO_MODO: Record<string, string> = {
  open: 'Aberto',
  register: 'Cadastro',
  hubsoft: 'HubSoft',
  manual: 'Manual',
};

interface HealthDto {
  status: string;
  database: boolean;
  pendingMigrations: string[];
  uptimeSeconds: number;
  version: string;
}

export function DashboardPage() {
  const { can, session } = useAuth();
  const podeVer = can(PERMISSIONS.REPORT_READ);

  const [dias, setDias] = useState<number>(30);
  const [mikrotikId, setMikrotikId] = useState<number | null>(null);
  const [vlan, setVlan] = useState<string | null>(null);
  const [equipamentos, setEquipamentos] = useState<MikrotikDto[]>([]);
  // As opções do filtro de VLAN ficam fora de `dados`: recarregar não pode
  // esvaziar o select enquanto a resposta não chega.
  const [localidades, setLocalidades] = useState<DashboardDto['localidades']>([]);
  const [dados, setDados] = useState<DashboardDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthDto | null>(null);

  useEffect(() => {
    // Banco desatualizado faz tudo falhar com erro interno; avisar aqui poupa
    // a caçada. Ver a guarda de migrations pendentes em server.ts.
    apiRequest<HealthDto>('/health')
      .then(setHealth)
      .catch(() => setHealth(null));
  }, []);

  useEffect(() => {
    if (!podeVer) return;
    // Sem permissão de ver equipamentos a lista vem vazia, e o filtro some.
    apiList<MikrotikDto>('/admin/mikrotiks?perPage=100')
      .then(({ data }) => setEquipamentos(data))
      .catch(() => setEquipamentos([]));
  }, [podeVer]);

  const consulta = useMemo(() => {
    const partes = [`dias=${dias}`];
    if (mikrotikId !== null) partes.push(`mikrotikId=${mikrotikId}`);
    if (vlan) partes.push(`vlan=${encodeURIComponent(vlan)}`);
    return partes.join('&');
  }, [dias, mikrotikId, vlan]);

  useEffect(() => {
    if (!podeVer) return;

    let ativo = true;
    setDados(null);
    setErro(null);

    apiRequest<DashboardDto>(`/admin/reports/dashboard?${consulta}`)
      .then((resposta) => {
        if (!ativo) return;
        setDados(resposta);
        setLocalidades(resposta.localidades);
      })
      .catch(
        (caught: unknown) =>
          ativo &&
          setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar'),
      );

    return () => {
      ativo = false;
    };
  }, [consulta, podeVer]);

  const localidadesDoFiltro = localidades.filter(
    (local) => mikrotikId === null || local.mikrotikId === mikrotikId,
  );
  const migracoesPendentes = health?.pendingMigrations ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">
            {podeVer
              ? `Últimos ${dias} dias${vlan ? ` · ${nomeDaLocalidade(vlan)}` : ''}, no fuso de Brasília.`
              : `Bem-vindo, ${session?.admin.name ?? ''}.`}
          </p>
        </div>

        {podeVer ? (
          <div className="flex flex-wrap items-center gap-3">
            {equipamentos.length > 0 ? (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                Equipamento
                <select
                  value={mikrotikId ?? ''}
                  onChange={(evento) => {
                    setMikrotikId(evento.target.value ? Number(evento.target.value) : null);
                    setVlan(null);
                  }}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm"
                >
                  <option value="">Todos</option>
                  {equipamentos.map((equipamento) => (
                    <option key={equipamento.id} value={equipamento.id}>
                      {equipamento.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {localidadesDoFiltro.length > 0 ? (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                VLAN
                <select
                  value={vlan ?? ''}
                  onChange={(evento) => setVlan(evento.target.value || null)}
                  className="max-w-64 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm"
                >
                  <option value="">Todas</option>
                  {localidadesDoFiltro.map((local) => (
                    <option key={`${local.mikrotikId}|${local.vlan}`} value={local.vlan}>
                      {nomeDaLocalidade(local.vlan)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <div className="flex rounded-xl bg-white p-1 text-sm shadow-card ring-1 ring-slate-200/70">
              {JANELAS.map((janela) => (
                <button
                  key={janela}
                  type="button"
                  onClick={() => setDias(janela)}
                  className={`rounded-lg px-3 py-1.5 font-semibold transition-colors ${
                    dias === janela
                      ? 'bg-brand-600 text-white shadow-sm shadow-brand-600/25'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {janela} dias
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </header>

      {migracoesPendentes.length > 0 ? (
        <Alert tone="error">
          <strong>Banco desatualizado:</strong> {migracoesPendentes.length} migração(ões) por
          aplicar. Funcionalidades novas vão falhar com erro interno até isso ser resolvido. Rode{' '}
          <code className="font-mono">migrate.js</code> no servidor — o runbook tem o comando em
          "Atualizar".
        </Alert>
      ) : null}

      {!podeVer ? (
        <Alert tone="info">
          Seu perfil não tem acesso aos relatórios. Use o menu para chegar às telas disponíveis.
        </Alert>
      ) : (
        <ConectadosAgora
          mikrotikId={mikrotikId}
          vlan={vlan}
          variosEquipamentos={equipamentos.length > 1}
        />
      )}

      {!podeVer ? null : erro ? (
        <Alert>{erro}</Alert>
      ) : !dados ? (
        <Spinner label="Somando os números..." />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Indicador
              rotulo="Acessos hoje"
              valor={dados.resumo.acessosHoje}
              comparacao={comparar(dados.resumo.acessosHoje, dados.resumo.acessosOntem)}
            />
            <Indicador
              rotulo="Aparelhos hoje"
              valor={dados.resumo.dispositivosHoje}
              nota="distintos"
            />
            <Indicador
              rotulo="Clientes novos hoje"
              valor={dados.resumo.clientesNovosHoje}
              nota={`${dados.resumo.clientesTotal} no total`}
            />
            <Indicador
              rotulo="Equipamentos online"
              valor={dados.resumo.equipamentosOnline}
              nota={`de ${dados.resumo.equipamentosTotal}`}
              alerta={dados.resumo.equipamentosOnline < dados.resumo.equipamentosTotal}
            />
          </div>

          <Card title={`Acessos por dia — ${dados.resumo.acessosNaJanela} no período`}>
            <GraficoPorDia pontos={dados.porDia} />
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card title="Por VLAN">
              <GraficoDeFatias
                fatias={dados.porLocalidade.map((fatia) => ({
                  rotulo: nomeDaLocalidade(fatia.rotulo),
                  total: fatia.total,
                }))}
              />
            </Card>

            <Card title="Por modo de acesso">
              <GraficoDeFatias
                fatias={dados.porModo.map((fatia) => ({
                  ...fatia,
                  rotulo: ROTULO_MODO[fatia.rotulo] ?? fatia.rotulo,
                }))}
              />
            </Card>

            {/* Com um equipamento só, a barra única de 100% não diz nada. */}
            {dados.porEquipamento.length > 1 ? (
              <Card title="Por equipamento">
                <GraficoDeFatias fatias={dados.porEquipamento} />
              </Card>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/** Variação em relação a ontem. Nulo quando ontem não teve movimento. */
function comparar(hoje: number, ontem: number): { texto: string; subiu: boolean } | null {
  if (ontem === 0) return null;

  const variacao = Math.round(((hoje - ontem) / ontem) * 100);
  if (variacao === 0) return { texto: 'igual a ontem', subiu: true };

  return {
    texto: `${variacao > 0 ? '+' : ''}${variacao}% vs ontem`,
    subiu: variacao > 0,
  };
}

function Indicador({
  rotulo,
  valor,
  nota,
  comparacao,
  alerta = false,
}: {
  rotulo: string;
  valor: number;
  nota?: string;
  comparacao?: { texto: string; subiu: boolean } | null;
  alerta?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl bg-white p-4 shadow-card ring-1 ${
        alerta ? 'ring-amber-300' : 'ring-slate-200/70'
      }`}
    >
      <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">{rotulo}</p>
      <p className="mt-2 text-3xl font-bold tabular-nums text-slate-900">{valor}</p>

      {comparacao ? (
        <p
          className={`mt-1 text-xs font-medium ${
            comparacao.subiu ? 'text-emerald-700' : 'text-amber-700'
          }`}
        >
          {comparacao.texto}
        </p>
      ) : nota ? (
        <p className="mt-1 text-xs text-slate-500">{nota}</p>
      ) : null}
    </div>
  );
}
